import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMusic } from "../lib/music";
import { isMuted as readVoiceMuted, stopVoice } from "../lib/voice";

type DamonBark = {
  id: string;
  trigger?: string;
  file: string;
  text?: string;
};

type DamonManifest = {
  voice?: string;
  barks?: DamonBark[];
};

const COOLDOWN_MS = 45_000;
const IDLE_MS = 90_000;
let damonManifestPromise: Promise<DamonManifest | null> | null = null;

function todayKey(): string {
  return new Date().toLocaleDateString("en-CA");
}

function barkSrc(file: string): string {
  return file.startsWith("/") ? file : `/damon-barks/${file}`;
}

export function dispatchDamonBark(id: string): void {
  window.dispatchEvent(new CustomEvent("damon:bark", { detail: { id } }));
}

export function DamonBarks(): null {
  const location = useLocation();
  const music = useMusic();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playedRef = useRef<Set<string>>(new Set());
  const lastPlayedAtRef = useRef(0);
  const gestureReadyRef = useRef(false);
  const pendingRef = useRef<string | null>(null);
  const mutedRef = useRef(readVoiceMuted());
  const [gestureReady, setGestureReady] = useState(false);

  const loadManifest = () => {
    if (!damonManifestPromise) {
      damonManifestPromise = fetch("/damon-barks/manifest.json")
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return damonManifestPromise;
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    } catch {}
    audioRef.current = null;
    try { music.unduck(); } catch {}
  };

  const play = (id: string) => {
    if (!id) return;
    if (!gestureReadyRef.current) {
      pendingRef.current = id;
      return;
    }
    mutedRef.current = readVoiceMuted();
    if (mutedRef.current) return;
    const now = Date.now();
    if (now - lastPlayedAtRef.current < COOLDOWN_MS) return;

    void loadManifest().then((manifest) => {
      const barks = manifest?.barks ?? [];
      if (!barks.length || readVoiceMuted()) return;
      const played = playedRef.current;
      if (played.size >= barks.length) played.clear();
      if (played.has(id)) return;
      const bark = barks.find((item) => item.id === id);
      if (!bark?.file) return;

      try {
        stopVoice();
        stop();
        const audio = new Audio(barkSrc(bark.file));
        audio.volume = 0.88;
        audioRef.current = audio;
        played.add(id);
        lastPlayedAtRef.current = Date.now();
        try { music.duck(); } catch {}
        const release = () => {
          if (audioRef.current === audio) audioRef.current = null;
          try { music.unduck(); } catch {}
        };
        audio.addEventListener("ended", release, { once: true });
        audio.addEventListener("error", release, { once: true });
        audio.play().catch(release);
      } catch {
        try { music.unduck(); } catch {}
      }
    });
  };

  useEffect(() => {
    loadManifest();
  }, []);

  useEffect(() => {
    const onGesture = () => {
      if (gestureReadyRef.current) return;
      gestureReadyRef.current = true;
      setGestureReady(true);
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) play(pending);
    };
    window.addEventListener("pointerdown", onGesture, { capture: true });
    window.addEventListener("keydown", onGesture, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", onGesture, { capture: true });
      window.removeEventListener("keydown", onGesture, { capture: true });
    };
  }, []);

  useEffect(() => {
    const onBark = (event: Event) => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (id) play(id);
    };
    const onMuteChanged = () => {
      mutedRef.current = readVoiceMuted();
      if (mutedRef.current) stop();
    };
    const onHelpOpen = () => play("help-open");
    window.addEventListener("damon:bark", onBark);
    window.addEventListener("damon:mute-changed", onMuteChanged);
    window.addEventListener("njrc:open-assistant", onHelpOpen);
    return () => {
      window.removeEventListener("damon:bark", onBark);
      window.removeEventListener("damon:mute-changed", onMuteChanged);
      window.removeEventListener("njrc:open-assistant", onHelpOpen);
      stop();
    };
  }, [music.duck, music.unduck]);

  useEffect(() => {
    if (!gestureReady) return;
    const hour = new Date().getHours();
    if (hour >= 22) {
      const key = `damon:late-night:${todayKey()}`;
      try {
        if (localStorage.getItem(key) !== "1") {
          localStorage.setItem(key, "1");
          play("late-night");
          return;
        }
      } catch {}
    }

    if (location.pathname === "/today") play("dashboard");
    else if (/^\/records\/[^/]+$/.test(location.pathname)) play("open-person");
  }, [gestureReady, location.pathname]);

  useEffect(() => {
    let timer: number | undefined;
    const arm = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => play("idle"), IDLE_MS);
    };
    const onActivity = () => arm();
    window.addEventListener("pointerdown", onActivity);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("scroll", onActivity, { passive: true });
    arm();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("scroll", onActivity);
    };
  }, []);

  useEffect(() => {
    const onLoginSuccess = () => {
      const key = `damon:first-login-day:${todayKey()}`;
      try {
        if (localStorage.getItem(key) !== "1") {
          localStorage.setItem(key, "1");
          play("first-login-day");
          return;
        }
      } catch {}
      play("login");
    };
    window.addEventListener("damon:login-success", onLoginSuccess);
    return () => window.removeEventListener("damon:login-success", onLoginSuccess);
  }, []);

  return null;
}
